import { useCallback } from 'react';
import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from 'react';
import type { HandleType } from '../../core';
import { useStoreApi } from '../../store';
import { clickConnect } from './clickConnect';
import { startConnectionDrag } from './connectionDrag';

const PRIMARY_BUTTON = 0;

export type HandleConnectOptions = {
  nodeId: string | null;
  handleId: string | null;
  type: HandleType;
  isConnectableStart: boolean;
};

/**
 * React handlers for a `<Handle>`. They are React props (not native listeners) so a child that stops propagation —
 * the app's "+" button — keeps its click from starting a click-to-connect, as in v11.
 */
export const useHandleConnect = ({ nodeId, handleId, type, isConnectableStart }: HandleConnectOptions) => {
  const store = useStoreApi();

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!nodeId || !isConnectableStart || event.button !== PRIMARY_BUTTON) return;
      startConnectionDrag(store, event.nativeEvent, event.currentTarget, { nodeId, handleId, type });
    },
    [store, nodeId, handleId, type, isConnectableStart],
  );

  const onClick = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (!nodeId) return;
      clickConnect(store, event.currentTarget.ownerDocument, { nodeId, handleId, type }, isConnectableStart);
    },
    [store, nodeId, handleId, type, isConnectableStart],
  );

  return { onPointerDown, onClick };
};
